/**
 * Formatierungsfunktionen für die Anwendung
 */

/**
 * Formatiert einen Betrag in eine lesbare Währungsdarstellung
 * @param amount - Der zu formatierende Betrag
 * @param currency - Die Währung (Standard: "EUR")
 * @param locale - Das Gebietsschema (Standard: "de-DE")
 * @returns Formatierter Betrag als Währungsstring
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'EUR',
  locale: string = 'de-DE'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Alle Formen, in denen in der App ein „Datum" vorkommt.
 * `{ toDate }` deckt Firestore-`Timestamp` strukturell ab (kein firebase-Import),
 * `{ seconds }` den serialisierten Timestamp (AccountPage-Muster).
 * (M-A, architecture-ux-density-v2.md §5.6)
 */
export type DateInput =
  | string
  | number
  | Date
  | { toDate: () => Date }
  | { seconds: number }
  | null
  | undefined;

/** Optionen für {@link formatDate}. */
export interface FormatDateOptions {
  /** i18n-Sprachcode ('de','en','fr','ru','pl','ro') ODER voller BCP-47-Tag. Default: 'de' → de-DE. */
  locale?: string;
  /** Uhrzeit anhängen (HH:MM, locale-konform). Default false. */
  includeTime?: boolean;
}

/**
 * Fixe Sprach→BCP-47-Locale-Zuordnung (europäische Formate, NIE en-US).
 * Unbekannte Codes fallen auf de-DE (europäischer Default) zurück.
 * (architecture-ux-density-v2.md §5.6)
 */
export const DATE_LOCALES: Record<string, string> = {
  de: 'de-DE',
  en: 'en-GB',
  fr: 'fr-FR',
  ru: 'ru-RU',
  pl: 'pl-PL',
  ro: 'ro-RO',
};

/** Anzeige-Konvention der App für „kein/ungültiges Datum" (EM-Dash). */
const EMPTY_DATE = '—';

/** Reines `YYYY-MM-DD` (ohne Zeitanteil). */
const DATE_ONLY_ISO = /^\d{4}-\d{2}-\d{2}$/;

function hasToDate(value: object): value is { toDate: () => Date } {
  return typeof (value as { toDate?: unknown }).toDate === 'function';
}

function hasSeconds(value: object): value is { seconds: number } {
  return typeof (value as { seconds?: unknown }).seconds === 'number';
}

/**
 * Wandelt einen beliebigen {@link DateInput} in ein valides `Date` um, oder
 * `null`, wenn der Wert fehlt/unparsebar ist. Wirft NIE.
 */
function resolveDate(date: DateInput): Date | null {
  if (date === null || date === undefined || date === '') return null;

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'number') {
    const fromNumber = new Date(date);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  if (typeof date === 'string') {
    // Reine ISO-Datumsstrings als LOKALES Datum parsen (nicht UTC), sonst
    // kippt '2025-11-03' in westlichen Zeitzonen auf den Vortag.
    if (DATE_ONLY_ISO.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      const local = new Date(year, month - 1, day);
      return Number.isNaN(local.getTime()) ? null : local;
    }
    const fromString = new Date(date);
    return Number.isNaN(fromString.getTime()) ? null : fromString;
  }

  if (typeof date === 'object') {
    if (hasToDate(date)) {
      try {
        const fromTs = date.toDate();
        return fromTs instanceof Date && !Number.isNaN(fromTs.getTime()) ? fromTs : null;
      } catch {
        return null;
      }
    }
    if (hasSeconds(date)) {
      const fromSeconds = new Date(date.seconds * 1000);
      return Number.isNaN(fromSeconds.getTime()) ? null : fromSeconds;
    }
  }

  return null;
}

/**
 * Formatiert ein Datum locale-fähig im europäischen Tag-Monat-Jahr-Format.
 *
 * Wirft NIE — jeder nicht interpretierbare Input ergibt `'—'` (Anzeige-Konvention
 * der App), niemals `'Invalid Date'`. Ausgabe stets via `Intl.DateTimeFormat`.
 *
 * @param date - beliebiger {@link DateInput} (String/Number/Date/Timestamp/null/…)
 * @param options - {@link FormatDateOptions} (locale, includeTime)
 * @returns Formatiertes Datum oder `'—'`
 * @example formatDate('2025-11-03')                    // '03.11.2025'
 * @example formatDate('2025-11-03', { locale: 'en' })  // '03/11/2025' (en-GB)
 * @example formatDate(undefined)                       // '—'
 */
export const formatDate = (date: DateInput, options: FormatDateOptions = {}): string => {
  const resolved = resolveDate(date);
  if (!resolved) return EMPTY_DATE;

  const { locale = 'de', includeTime = false } = options;
  const bcp47 = DATE_LOCALES[locale] ?? (locale.includes('-') ? locale : 'de-DE');

  const intlOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  if (includeTime) {
    intlOptions.hour = '2-digit';
    intlOptions.minute = '2-digit';
  }

  try {
    return new Intl.DateTimeFormat(bcp47, intlOptions).format(resolved);
  } catch {
    // Unbekannter/kaputter Locale-Tag ⇒ auf de-DE zurückfallen, nie werfen.
    return new Intl.DateTimeFormat('de-DE', intlOptions).format(resolved);
  }
};

/**
 * Formatiert eine Telefonnummer in lesbares Format
 * @param phoneNumber - Die zu formatierende Telefonnummer
 * @returns Formatierte Telefonnummer
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // Entferne alle Nicht-Ziffern
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Einfache deutsche Formatierung (kann je nach Anforderung angepasst werden)
  if (digits.startsWith('49') && digits.length >= 10) {
    // International: +49 12 3456789
    return `+${digits.substring(0, 2)} ${digits.substring(2, 4)} ${digits.substring(4)}`;
  } else if (digits.length >= 10) {
    // National: 0123 456789
    return `${digits.substring(0, 4)} ${digits.substring(4)}`;
  }
  
  // Fallback bei unbekanntem Format
  return phoneNumber;
}; 